import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL } from '../config';

interface ConferenceSettings {
  accepting_sponsors: boolean;
  accepting_cfp: boolean;
}

interface ConferenceSettingsContextType {
  settings: ConferenceSettings | null;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
}

const ConferenceSettingsContext = createContext<ConferenceSettingsContextType>({
  settings: null,
  loading: true,
  error: null,
  refreshSettings: async () => {},
});

export const useConferenceSettings = () => useContext(ConferenceSettingsContext);

interface ConferenceSettingsProviderProps {
  children: ReactNode;
}

export const ConferenceSettingsProvider: React.FC<ConferenceSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<ConferenceSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/public/conference-settings`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch conference settings');
      }
      
      const data = await response.json();
      setSettings(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching conference settings:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    await fetchSettings();
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <ConferenceSettingsContext.Provider
      value={{
        settings,
        loading,
        error,
        refreshSettings,
      }}
    >
      {children}
    </ConferenceSettingsContext.Provider>
  );
};

export default ConferenceSettingsContext;
